import { useMemo, useState } from "react";
import { errorAlertMessageFrom } from "../../error";
import { useOrganisationalUnitsQuery } from "../../graphql/query";
import { useCreateMatterTemplateMutation } from "./use-create-matter-template";
import { createMatterTemplate_createMatterTemplate } from "../../graphql/__generated_types__/createMatterTemplate";
import { MatterCreateInput } from "../../graphql/__generated_types__/globalTypes";
import { AlertMessage } from "@design-system";
import { OperationVariables } from "@apollo/client";

export type CreatedMatterTemplate = createMatterTemplate_createMatterTemplate;
export type PracticeArea = { id: number; name: string };

export interface MatterTemplateFormViewModel {
  loading(): boolean;
  saving(): boolean;
  practiceAreas(): PracticeArea[] | undefined;
  alertMessage(): AlertMessage | undefined;
  createMatterTemplate(input: MatterCreateInput, cloneTemplateId?: number): void;
}

type State = {
  alertMessage?: AlertMessage;
  saving: boolean;
};
const INITIAL_STATE: State = { saving: false };

export function useMatterTemplateFormViewModel(
  onCreated: (id: number) => void,
  refetchVariables: OperationVariables
): MatterTemplateFormViewModel {
  const [state, setState] = useState<State>(INITIAL_STATE);
  const { saving, alertMessage } = state;
  const { loading: loadingPracticeAreas, data: practiceAreasData } = useOrganisationalUnitsQuery({
    onError: (err) =>
      setState({
        ...state,
        alertMessage: errorAlertMessageFrom(err, "Failed to load practice areas"),
      }),
  });
  const [createMatterTemplate] = useCreateMatterTemplateMutation(refetchVariables, {
    onError: (err) => {
      setState({
        ...state,
        saving: false,
        alertMessage: errorAlertMessageFrom(err, "Failed to create matter template"),
      });
    },
    onCompleted: ({ createMatterTemplate: { id } }) => onCreated(id),
  });

  return useMemo(
    () => ({
      loading: () => loadingPracticeAreas,
      saving: () => saving,
      practiceAreas: () => (practiceAreasData ? practiceAreasData.organisationalUnits : undefined),
      alertMessage: () => alertMessage,
      createMatterTemplate: async (input, cloneTemplateId) =>
        createMatterTemplate(input, cloneTemplateId),
    }),
    [loadingPracticeAreas, practiceAreasData, alertMessage, createMatterTemplate]
  );
}
